import { GenericApiResponse, Header, KDE, Pagination } from '../general-store/general.model';

/** Cooldown in ms before CSV export can be triggered again (avoids spamming jobs/emails). */
export const EXPORT_CSV_COOLDOWN_MS = 60_000;

export interface ReportStateModel {
  isProcessing: boolean;
  /** True while CSV export request is in flight. */
  exportCsvInProgress: boolean;
  /** Timestamp (ms) until which CSV export button is disabled after a successful trigger. */
  exportCsvCooldownUntil: number | null;
  reports: Report[];
  report: Report | null;
  reportData: ReportData;
  pagination: Pagination;
  headers: Header[];
  reportHeaders: Header[];
  visualizationData: Record<number, VisualizationData>;
  farmerReceipt: FarmerReceipt;
  transferVoucher: TransferVoucher;
  receiving: Receiving;
  reconciliation: Reconciliation;
  bagTraceability: BagTraceability;
  transactionsSummary: TransactionsSummary;
}

export interface FarmerReceipt {
  rows: {
    qrcode: string;
    label: string;
    value: {
      value: string;
    };
    createdAt: string;
  }[];
  total: number;
  totalBags?: number;
}

export interface TransferVoucher {
  rows: any[];
  total: number;
  totalFarmers?: number;
  totalBags?: number;
}

export interface Receiving {
  rows: {
    qrcode: string;
    label: string;
    value: {
      value: string;
    };
    createdAt: string;
  }[];
  total: number;
}

export interface Reconciliation {
  rows: {
    qrcode: string;
    label: string;
    value: {
      value: string;
    };
    createdAt: string;
  }[];
  total: number;
  totalReceived: number;
}

export interface BagTraceability {
  rows: {
    date: string;
    qrcode: string;
    type: string;
    certification: any;
    farmer: string;
    farmerId: string;
    lotId: string;
    batchId: string;
    warehouse: string;
    bagContributions: number;
    weight: number;
    position: string;
    farmerName: string;
  }[];
  total: number;
}

export interface TransactionsSummary {
  rows: {
    purchaseDate: string;
    farmerId: string;
    farmerName: string;
    numberOfBags: number;
    weight: number;
  }[];
  totalBags: number;
  totalWeight: number;
}

export interface VisualizationData {
  chartData: ChartData[];
  config: any;
}

export interface ChartData {
  time: string;
  value: number;
  label: string;
}

export interface Report {
  id?: number;
  reportName: string;
  description: string;
  filters: ReportFilters;
  visualizations: Visualization[];
  scheduleReport: ScheduleReportPayload | null;
}
export interface ReportData {
  rows: Record<string, any>[];
  aggregation: {
    average: number;
    count: number;
    field: string;
    max: number;
    min: number;
    sum: number;
  } | null;
}

export interface Visualization {
  id: number;
  title: string;
  description: string;
  visualizationType: string;
  config: {
    title: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    showLegend?: boolean;
    colors?: string[];
    lineStyle?: string;
    barOrientation?: string;
    timeInterval?: string;
    aggregation?: string;
  };
  dataConfig: {
    kdes: KDE[];
    timeField?: string;
    valueField?: string;
    groupingField?: string;
    categoryKey?: string;
    labelKey?: string;
    categoryField?: string;
    seriesField?: string;
    xField?: string;
    yField?: string;
  };
}

export interface ReportResponse extends GenericApiResponse {
  payload: Report[];
}

export interface ReportDataResponse extends GenericApiResponse {
  payload: ReportData;
}

export interface FarmerReceiptResponse extends GenericApiResponse {
  payload: FarmerReceipt;
}

export interface ReceivingResponse extends GenericApiResponse {
  payload: Receiving;
}

export interface ReconciliationResponse extends GenericApiResponse {
  payload: Reconciliation;
}

export interface BagTraceabilityResponse extends GenericApiResponse {
  payload: BagTraceability;
}

export interface TransactionsSummaryResponse extends GenericApiResponse {
  payload: TransactionsSummary;
}

export interface VisualizationDataResponse extends GenericApiResponse {
  payload: VisualizationData;
}

export interface VisualizationResponse extends GenericApiResponse {
  payload: Visualization;
}

export interface ReportFilters {
  productId: number;
  dateRange: {
    start: string;
    end: string;
  };
  includeGrouping: string;
  kdes: KDE[];
}

export interface FarmerReceiptPayload {
  filters: {
    farmer: number;
  };
}

export interface CreateReportPayload {
  reportName: string;
  description: string;
  filters: ReportFilters;
}

export interface ScheduleReportPayload {
  reportId: number;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  frequency: string;
  deliveryTime: string;
  /** 0=Sunday … 6=Saturday; array for multi-day weekly schedules */
  dayOfWeek?: number | number[];
  dayOfMonth?: number;
  recipients: string[];
}

export interface CreateVisualizationPayload {
  title: string;
  description: string;
  visualizationType: string;
  config: {
    title: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    showLegend?: boolean;
    colors?: string[];
    lineStyle?: string;
    barOrientation?: string;
    timeInterval?: string;
    aggregation?: string;
  };
  dataConfig: {
    kdes: KDE[];
    timeField?: string;
    valueField?: string;
    groupingField?: string;
    categoryKey?: string;
    labelKey?: string;
    categoryField?: string;
    seriesField?: string;
    xField?: string;
    yField?: string;
  };
}

export interface CreateReportResponse {
  status: string;
  payload: Report;
  message: string;
  error: any;
}

export interface ScheduleReportResponse {
  status: string;
  message: string;
  error: any;
}

export interface Graph {
  label: string;
  icon: string;
  description: string;
  type: string;
}
